import { useEffect, useState, useCallback, useRef } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";

interface UsePWAReturn {
  needRefresh: boolean;
  offlineReady: boolean;
  updateServiceWorker: () => Promise<void>;
  dismissUpdate: () => void;
  checkForUpdate: () => Promise<void>;
}

export function usePWA(): UsePWAReturn {
  const [dismissed, setDismissed] = useState(false);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, registration) {
      console.log("[PWA] Service Worker registered:", swUrl);
      registrationRef.current = registration || null;
      
      // Check for updates every 60 minutes
      if (registration) {
        setInterval(() => {
          console.log("[PWA] Periodic check for updates...");
          registration.update().catch(console.error);
        }, 60 * 60 * 1000);
      }
    },
    onRegisterError(error) {
      console.error("[PWA] Service Worker registration error:", error);
    },
    onNeedRefresh() {
      console.log("[PWA] New content available, showing update prompt");
      setDismissed(false);
    },
    onOfflineReady() {
      console.log("[PWA] App ready to work offline");
    },
  });

  // Manual check for update function
  const checkForUpdate = useCallback(async () => {
    console.log("[PWA] Manual check for updates triggered");

    try {
      let registration = registrationRef.current;

      if (!registration && "serviceWorker" in navigator) {
        registration = await navigator.serviceWorker.getRegistration();
        registrationRef.current = registration || null;
      }

      if (!registration) {
        console.log("[PWA] No registration available for update check");
        return;
      }

      await registration.update();
      console.log("[PWA] Update check completed");

      if (registration.waiting) {
        console.log("[PWA] Found waiting service worker, prompting update");
        setNeedRefresh(true);
        setDismissed(false);
      }
    } catch (error) {
      console.error("[PWA] Error checking for updates:", error);
    }
  }, [setNeedRefresh]);

  const handleUpdate = useCallback(async () => {
    console.log("[PWA] User triggered update");
    localStorage.removeItem("pwa-update-dismissed");
    await updateServiceWorker(true);
  }, [updateServiceWorker]);

  const dismissUpdate = useCallback(() => {
    console.log("[PWA] User dismissed update notification");
    setDismissed(true);
    
    // Store dismissal time to potentially auto-update later
    localStorage.setItem("pwa-update-dismissed", Date.now().toString());
    
    // Auto-update after 24 hours if still pending
    setTimeout(() => {
      const dismissedAt = localStorage.getItem("pwa-update-dismissed");
      if (dismissedAt && needRefresh) {
        const hoursSinceDismissal = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60);
        if (hoursSinceDismissal >= 24) {
          console.log("[PWA] Auto-updating after 24 hours");
          updateServiceWorker(true);
        }
      }
    }, 24 * 60 * 60 * 1000);
  }, [needRefresh, updateServiceWorker]);

  // Check for updates when app comes to foreground
  useEffect(() => {
    const runUpdateCheck = () => {
      console.log("[PWA] App active, checking for updates");

      if (registrationRef.current?.waiting) {
        console.log("[PWA] Found waiting SW on app active");
        setNeedRefresh(true);
        setDismissed(false);
      } else {
        checkForUpdate();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        runUpdateCheck();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", runUpdateCheck);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", runUpdateCheck);
    };
  }, [checkForUpdate, setNeedRefresh]);

  // Check on initial load if there's a waiting SW
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registrationRef.current = registration;
        
        if (registration.waiting) {
          console.log("[PWA] Found waiting SW on load");
          setNeedRefresh(true);
          setDismissed(false);
        }
        
        // Listen for new service worker installation
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          console.log("[PWA] Update found, new SW installing");
          
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log("[PWA] New SW installed and waiting");
                setNeedRefresh(true);
                setDismissed(false);
              }
            });
          }
        });
      });
      
      // Handle controller change (when skipWaiting is called)
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing) {
          console.log("[PWA] Controller changed, reloading page");
          refreshing = true;
          window.location.reload();
        }
      });
    }
  }, [setNeedRefresh]);

  // Check if update was dismissed but app was restarted
  useEffect(() => {
    const dismissedAt = localStorage.getItem("pwa-update-dismissed");
    if (dismissedAt && needRefresh) {
      const hoursSinceDismissal = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60);
      // If more than 6 hours since dismissal and there's an update, show prompt again
      if (hoursSinceDismissal >= 6) {
        localStorage.removeItem("pwa-update-dismissed");
        setDismissed(false);
      } else {
        setDismissed(true);
      }
    }
  }, [needRefresh]);

  return {
    needRefresh: needRefresh && !dismissed,
    offlineReady,
    updateServiceWorker: handleUpdate,
    dismissUpdate,
    checkForUpdate,
  };
}
